/* eslint-disable @typescript-eslint/no-explicit-any */
import { CSSProperties, ComponentProps, ReactNode } from 'react';
import styleToCss from 'style-object-to-css-string';
import styled from 'styled-components';
import { Box, Sizes, Typography } from '@altermaind/web/ui';
import { Tooltip } from '../Tooltip';

const defaultRowHoverStyle: CSSProperties = {
  backgroundColor: 'grey/dark',
  borderRadius: '10px',
};

export type RowItemRenderValue = string | ((item: any) => ReactNode);

export enum SortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export type OrderBy = {
  [columnKey: string]: `${SortDirection}`;
};

export type Column = {
  key: string;
  title: string | ReactNode;
  titleTooltip?: string;
  titleTooltipProps?: Partial<Omit<ComponentProps<typeof Tooltip>, 'children'>>;
  columnProps?: Partial<Omit<ComponentProps<typeof Box>, 'children'>>;
  renderValue: RowItemRenderValue;
  width?: string | number;
  fitContent?: boolean;
  sortable?: boolean;
  sortKey?: string;
};

/**
 * Discriminated union type for controlling row selection in the Table component.
 *
 * When `showCheckboxes` is `true`, the following props are required:
 * - `selectedItems` — array of currently selected row IDs (controlled state, managed by the parent)
 * - `onSelectionChange` — callback invoked with the updated selection array whenever a row or the header checkbox is toggled
 *
 * Optional when `showCheckboxes` is `true`:
 * - `getRowId` — custom function to extract the ID from a row item; defaults to `item.id`
 * - `bulkActionLabel` — label text for the bulk action button shown in the selection banner
 * - `bulkActionOnClick` — callback invoked with the current `selectedItems` when the bulk action button is clicked
 *
 * When `showCheckboxes` is `false` or omitted, no other props are accepted.
 *
 * @example
 * // Controlled usage with checkbox selection
 * const [selected, setSelected] = useState<string[]>([]);
 *
 * <Table
 *   showCheckboxes
 *   selectedItems={selected}
 *   onSelectionChange={setSelected}
 *   getRowId={(item) => item.employeeId}
 *   bulkActionLabel="Assign department"
 *   bulkActionOnClick={(ids) => handleBulkAssign(ids)}
 *   columns={columns}
 *   rowData={employees}
 * />
 */
export type CheckboxesProps =
  | {
      /** Must be `true` to enable row selection. */
      showCheckboxes: true;
      /** Controlled array of selected row IDs. */
      selectedItems: string[];
      /** Called with the updated selection array whenever a row or the header checkbox is toggled. */
      onSelectionChange: (items: string[]) => void;
      /** Extracts the unique ID from a row item. Defaults to `item.id`. */
      getRowId?: (item: any) => string;
      /** Label text for the bulk action button displayed in the selection banner. */
      bulkActionLabel?: string;
      /** Called with the current `selectedItems` when the bulk action button is clicked. */
      bulkActionOnClick?: (items: string[]) => void;
    }
  | {
      showCheckboxes?: false | undefined;
      selectedItems?: never;
      onSelectionChange?: never;
      getRowId?: never;
      bulkActionLabel?: never;
      bulkActionOnClick?: never;
    };

export type TableProps = ComponentProps<typeof Box> & {
  columns: Column[];
  rowData: any[];
  headerProps?: ComponentProps<typeof Box>;
  headerTestId?: string;
  rowTestId?: string;
  /**
   * @deprecated no longer supported
   */
  bodyProps?: ComponentProps<typeof Box>;
  columnWrapperProps?: ComponentProps<typeof Box>;
  columnTitleProps?:
    | ComponentProps<typeof Typography>
    | ((columnKey: string) => ComponentProps<typeof Typography>);
  rowWrapperProps?:
    | ComponentProps<typeof Box>
    | ((item: any, index: number) => ComponentProps<typeof Box>);
  cellContentProps?:
    | ComponentProps<typeof Box>
    | ((columnKey: string, item: any, index: number) => ComponentProps<typeof Box>);
  rowHoverStyle?: CSSProperties;
  defaultCellVariant?: string | any;
  columnGap?: keyof Sizes;
  onRowClick?: (item: any, index: number) => void;
  onHoverRow?: (item: any, index: number) => void;
  selectedRow?: number;
  hasHover?: boolean;
  orderBy?: OrderBy;
  onSort?: (orderBy: OrderBy) => void;
  showCheckboxes?: boolean;
} & CheckboxesProps;

export const StyledScrollBox = styled<any>(Box)`
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #ededed;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #b3b3b3;
    border-radius: 4px;
  }
`;

export const HoverStyledBox = styled<any>(Box)<Pick<TableProps, 'rowHoverStyle'>>`
  ${props =>
    props.rowHoverStyle
      ? `
	&:hover {
		${styleToCss({ ...defaultRowHoverStyle, ...props.rowHoverStyle })}
	}
	`
      : ''}
`;
